import { $, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { fillForm } from '../lib/form.js';
import { mountResourceEditor } from '../lib/resource-editor.js';
import { findSelectMenu } from '../lib/select-menu.js';

/**
 * The editor of a voice channel generator.
 *
 * The bitrate is entered in kbps and stored in bps: Discord counts in bits,
 * people in kilobits, and showing "64000" in a field labelled "bitrate" helps
 * nobody. The conversion lives here, at the edge, and nowhere else.
 */

interface Hub {
  _id: string;
  name: string;
  channelId: string;
  categoryId: string | null;
  nameTemplate: string;
  userLimit: number;
  bitrate: number | null;
  region: string | null;
  controlRoleIds: string[];
  protectedRoleIds: string[];
  enabled: boolean;
}

interface EditorPageData {
  hub: Hub;
  guildId: string;
  maxBitrate: number;
}

export function initTempVoiceEditorPage(): void {
  const form = $<HTMLFormElement>('[data-tempvoice-form]');
  if (!form) return;

  const { hub, guildId } = pageData<EditorPageData>();

  fillForm(form, {
    ...hub,
    // In kbps for entry; an empty field means "the server's default".
    bitrate: hub.bitrate === null ? '' : Math.round(hub.bitrate / 1000),
  });

  const channel = findSelectMenu(form, 'channelId');
  if (channel) channel.value = hub.channelId ? [hub.channelId] : [];

  const category = findSelectMenu(form, 'categoryId');
  if (category) category.value = hub.categoryId ? [hub.categoryId] : [];

  // Empty by default: a generator restricts controls to nobody until a server
  // names the roles that get them.
  const controlRoles = findSelectMenu(form, 'controlRoleIds');
  if (controlRoles) controlRoles.value = hub.controlRoleIds ?? [];

  const protectedRoles = findSelectMenu(form, 'protectedRoleIds');
  if (protectedRoles) protectedRoles.value = hub.protectedRoleIds ?? [];

  /*
   * `enabled` ticked by default: a generator somebody has just described should
   * work without their having to think of switching it on. `fillForm` cannot
   * guess that for a creation, where the document does not exist yet.
   */
  const enabled = form.elements.namedItem('enabled');
  if (enabled instanceof HTMLInputElement) enabled.checked = hub._id ? hub.enabled : true;

  mountResourceEditor({
    endpoint: 'tempvoice',
    listUrl: `/modules/tempvoice?guildId=${guildId}`,
    guildId,
    form,
    itemId: hub._id,

    serialize: () => {
      const kbps = Number((form.elements.namedItem('bitrate') as HTMLInputElement)?.value ?? '');

      return {
        name: text(form, 'name'),
        channelId: findSelectMenu(form, 'channelId')?.single ?? '',
        categoryId: findSelectMenu(form, 'categoryId')?.single || null,
        nameTemplate: text(form, 'nameTemplate') || '{user}',
        userLimit: Number(text(form, 'userLimit')) || 0,
        bitrate: Number.isFinite(kbps) && kbps > 0 ? kbps * 1000 : null,
        region: null,
        // Without these two the role pickers filled on load and saved nothing:
        // every generator persisted an empty list, so everyone got the panel
        // and no role was protected. The multiselect showed, the save dropped it.
        controlRoleIds: findSelectMenu(form, 'controlRoleIds')?.value ?? [],
        protectedRoleIds: findSelectMenu(form, 'protectedRoleIds')?.value ?? [],
        enabled: checked(form, 'enabled'),
        publish: false,
      };
    },

    labels: {
      saved: ct('tempVoiceHubSaved', 'Generator saved.'),
      confirmDelete: ct('confirmDeleteTempVoiceHub', 'Delete this generator?'),
      deleted: ct('tempVoiceHubDeleted', 'Generator deleted.'),
    },
  });
}

function text(form: HTMLFormElement, name: string): string {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement
    ? field.value.trim()
    : '';
}

function checked(form: HTMLFormElement, name: string): boolean {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? field.checked : false;
}
